import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { products } from '../assets/productsData';
import './Home.css';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1 },
  }),
};

const slideInLeft = {
  hidden: { opacity: 0, x: -42 },
  visible: (i = 0) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.72, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Home = () => {
  return (
    <main className="home">
      {/* HERO SECTION */}
      <section className="hero">
        <div 
          className="hero-img-bg" 
          style={{ backgroundImage: `url('/spices.png')` }}
        />
        <div className="hero-overlay" />

        <div className="hero-inner container">
          <div
            className="hero-content"
          >
            <motion.div
              className="hero-tag"
              initial="hidden"
              animate="visible"
              custom={0}
              variants={slideInLeft}
            >
              India's Emerging Agro Exporter
            </motion.div>
            <h1 className="hero-title">
              <motion.span
                initial="hidden"
                animate="visible"
                custom={1}
                variants={slideInLeft}
              >
                Carrying India's Finest
              </motion.span>
              <motion.span
                initial="hidden"
                animate="visible"
                custom={2}
                variants={slideInLeft}
              >
                Harvests to the <em>World</em>
              </motion.span>
            </h1>
            <motion.p
              className="hero-desc"
              initial="hidden"
              animate="visible"
              custom={3}
              variants={slideInLeft}
            >
              From the fertile heartlands of Uttar Pradesh, Seth Mr. Sanwariya Agro Primes
              delivers premium frozen foods, organic spices, foxnuts, and dehydrated vegetable
              powders to global markets with precision, integrity, and international-grade packaging.
            </motion.p>
            <motion.div
              className="hero-btns"
              initial="hidden"
              animate="visible"
              custom={4}
              variants={fadeUp}
            >
              <Link to="/products" className="btn-primary">Explore Products</Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <div className="trust-strip">
        <div className="container trust-inner">
          {[
            { icon: '✅', label: 'FSSAI Certified' },
            { icon: '🏛', label: 'APEDA Registered' },
            { icon: '📋', label: 'IEC Certified' },
            { icon: '🌍', label: 'International Export Ready' },
            { icon: '📦', label: 'Premium Export Packaging' },
          ].map((t) => (
            <div key={t.label} className="trust-item">
              <div className="trust-icon">{t.icon}</div>
              {t.label}
            </div>
          ))}
        </div>
      </div>

      {/* FEATURED PRODUCTS */}
      <section className="home-products">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div className="section-tag">Our Products</div>
            <div className="hp-header">
              <h2 className="section-title">Explore Our Product Range</h2>
              <Link to="/products" className="view-all-btn">View All Products →</Link>
            </div>
          </motion.div>
          
          <div className="hp-grid">
            {products.slice(0, 6).map((p, i) => (
              <motion.div
                key={p.id}
                className="hp-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.05}
                variants={fadeUp}
                style={{ '--card-bg': p.bgColor }}
              >
                <Link to={`/products?id=${p.id}`}>
                  <div className="hp-card-img">
                    <img src={p.image} alt={p.name} className="hp-product-img" />
                  </div>
                  <div className="hp-card-body">
                    <div className="product-cat">{p.catLabel}</div>
                    <div className="product-name">{p.name}</div>
                    <div className="product-desc">{p.desc.substring(0, 90)}...</div>
                    <div className="product-meta">
                      <span className="product-moq">MOQ: {p.moq}</span>
                      <div className="product-arrow">→</div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="why-section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div className="section-tag why-tag">Why Choose Us</div>
            <h2 className="section-title" style={{ color: 'var(--white)' }}>Built for Global Buyers</h2>
            <p className="section-sub" style={{ color: 'rgba(255,255,255,0.7)' }}>
              We combine the richness of Indian agriculture with the precision of international export standards.
            </p>
          </motion.div>
          <div className="why-grid">
            {[
              { icon: '🏅', title: 'Certified Quality', desc: 'FSSAI, APEDA & IEC certified. Every product meets rigorous food safety and export compliance standards.' },
              { icon: '📦', title: 'Premium Packaging', desc: 'Category-specific export packaging — METPET, BOPP, Vacuum, Matte Zipper — preserving quality from farm to shelf.' },
              { icon: '🤝', title: 'Transparent Trade', desc: 'No hidden costs. Clear pricing, documentation support, and end-to-end order tracking for all buyers.' },
              { icon: '🌿', title: 'Farm Fresh Source', desc: 'Direct sourcing from UP\'s finest farms ensures freshness, purity, and consistent product quality across batches.' },
              { icon: '🚢', title: 'Reliable Fulfillment', desc: '500kg MOQ with flexible bulk order capacity. Dedicated logistics coordination for smooth shipment.' },
              { icon: '📋', title: 'Full Documentation', desc: 'Certificate of Origin, Phytosanitary, FSSAI, APEDA, COA — all export documents arranged seamlessly.' },
            ].map((w, i) => (
              <motion.div
                key={w.title}
                className="why-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.1}
                variants={fadeUp}
              >
                <div className="why-icon">{w.icon}</div>
                <div className="why-title">{w.title}</div>
                <div className="why-desc">{w.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL REACH SECTION WITH WORLD MAP DASHBOARD */}
      <section className="reach-section">
        <div className="container">
          <div className="reach-grid">
            
            <motion.div
              className="reach-left-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="section-tag global-tag">✨ Market Expansion</div>
              <h2 className="section-title global-title">Targeting Key Strategic Markets Worldwide</h2>
              <p className="section-sub">
                We are actively expanding our footprint, establishing reliable supply chains with food importers, 
                bulk wholesalers, and multi-national retail distributors across high-compliance international markets.
              </p>

              <div className="countries-modern-grid">
                {[
                  { flag: '🇺🇸', code: 'USA', name: 'United States', status: 'Primary Target', type: 'high-demand' },
                  { flag: '🇨🇦', code: 'CAN', name: 'Canada', status: 'Primary Target', type: 'high-demand' },
                  { flag: '🇦🇪', code: 'UAE', name: 'United Arab Emirates', status: 'Priority Hub', type: 'priority' },
                  { flag: '🇸🇦', code: 'GCC', name: 'Gulf Countries', status: 'Priority Market', type: 'priority' },
                  { flag: '🇦🇺', code: 'AUS', name: 'Australia', status: 'Target Market', type: 'regular' },
                  { flag: '🇷🇺', code: 'RUS', name: 'Russia', status: 'Target Market', type: 'regular' },
                ].map((c) => (
                  <div key={c.code} className={`country-premium-card ${c.type}`}>
                    <div className="country-card-header">
                      <span className="country-premium-flag">{c.flag}</span>
                      <span className="country-premium-code">{c.code}</span>
                    </div>
                    <div className="country-card-body-info">
                      <div className="country-premium-name">{c.name}</div>
                      <div className="country-premium-status">
                        <span className="status-dot"></span> {c.status}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Rest of World Full Width Banner Card */}
                <div className="country-premium-card row-full-width">
                  <div className="row-card-inner">
                    <div className="row-card-text">
                      <div className="country-premium-name">Global Inquiries (Rest of the World)</div>
                      <div className="country-premium-status-open">Open to all custom bulk orders, direct shipping lines, & container logistics</div>
                    </div>
                  </div>
                  <div className="row-card-tag">Global Logistics Ready</div>
                </div>
              </div>
            </motion.div>

            {/* NEW UPGRADED WORLD MAP VISUAL WINDOW PANEL */}
            <motion.div
              className="world-digital-dashboard"
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="dashboard-glass-header">
                <div className="dashboard-dots"><span className="d-red"></span><span className="d-yellow"></span><span className="d-green"></span></div>
                <div className="dashboard-title-bar">SETH SANWARIYA AGRO · LIVE SUPPLY CHAIN</div>
              </div>

              <div className="dashboard-main-view">
                <svg viewBox="0 0 1000 500" xmlns="http://www.w3.org/2000/svg" className="premium-map-svg">
                  {/* Background Subtle Grid Coordinates */}
                  <g stroke="rgba(27, 67, 50, 0.04)" strokeWidth="1">
                    <line x1="0" y1="100" x2="1000" y2="100" />
                    <line x1="0" y1="200" x2="1000" y2="200" />
                    <line x1="0" y1="300" x2="1000" y2="300" />
                    <line x1="0" y1="400" x2="1000" y2="400" />
                    <line x1="200" y1="0" x2="200" y2="500" />
                    <line x1="400" y1="0" x2="400" y2="500" />
                    <line x1="600" y1="0" x2="600" y2="500" />
                    <line x1="800" y1="0" x2="800" y2="500" />
                  </g>

                  {/* Clean Vector World Continent Silhouettes */}
                  <g fill="rgba(64, 145, 108, 0.12)" className="map-continents">
                    {/* North America */}
                    <path d="M 100,100 L 280,100 L 250,220 L 210,250 L 150,200 Z" />
                    {/* South America */}
                    <path d="M 220,260 L 280,290 L 250,450 L 220,450 L 200,320 Z" />
                    {/* Europe & North Asia (Russia) */}
                    <path d="M 450,60 L 850,60 L 800,180 L 600,180 L 480,140 Z" />
                    {/* Africa */}
                    <path d="M 480,200 L 580,220 L 590,320 L 540,380 L 470,280 Z" />
                    {/* South Asia & India */}
                    <path d="M 600,190 L 660,190 L 680,240 L 640,270 L 620,240 Z" />
                    {/* East Asia & China */}
                    <path d="M 670,140 L 790,140 L 820,240 L 700,240 Z" />
                    {/* Australia */}
                    <path d="M 780,320 L 880,330 L 860,400 L 760,380 Z" />
                  </g>

                  {/* Dynamic Animated Shipping Routes Lines */}
                  <g fill="none" strokeWidth="2.5" strokeLinecap="round">
                    {/* India to USA / Canada */}
                    <path d="M 635,245 Q 380,80 180,140" stroke="var(--green)" strokeDasharray="6,4" className="shipping-line" />
                    {/* India to Russia */}
                    <path d="M 635,245 Q 640,140 680,95" stroke="var(--gold)" strokeDasharray="6,4" className="shipping-line" />
                    {/* India to UAE / Gulf */}
                    <path d="M 635,245 Q 570,240 530,230" stroke="var(--gold)" strokeDasharray="5,3" className="shipping-line" />
                    {/* India to Australia */}
                    <path d="M 635,245 Q 740,300 810,350" stroke="var(--green)" strokeDasharray="6,4" className="shipping-line" />
                  </g>

                  {/* ORIGIN STATION HUB MARKER (INDIA) */}
                  <g className="map-marker origin">
                    <circle cx="635" cy="245" r="15" fill="rgba(27, 67, 50, 0.25)" className="pulse-circle" />
                    <circle cx="635" cy="245" r="6" fill="var(--green)" />
                    <text x="635" y="275" textAnchor="middle" fontSize="14" fontWeight="800" fill="var(--green)">INDIA (📍 Hub)</text>
                  </g>

                  {/* GLOBAL DESTINATION TARGET PORTS */}
                  {/* USA / Canada */}
                  <g className="map-marker destination">
                    <circle cx="180" cy="140" r="6" fill="var(--green)" />
                    <text x="180" y="122" textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--green)">USA / CANADA</text>
                  </g>

                  {/* Russia */}
                  <g className="map-marker destination">
                    <circle cx="680" cy="95" r="5" fill="var(--gold)" />
                    <text x="694" y="98" fontSize="13" fontWeight="700" fill="#7a6010">RUSSIA</text>
                  </g>

                  {/* UAE / Gulf */}
                  <g className="map-marker destination">
                    <circle cx="530" cy="230" r="5" fill="var(--gold)" />
                    <text x="445" y="235" fontSize="13" fontWeight="700" fill="#7a6010">UAE & GCC</text>
                  </g>

                  {/* Australia */}
                  <g className="map-marker destination">
                    <circle cx="810" cy="350" r="6" fill="var(--green)" />
                    <text x="824" y="355" fontSize="13" fontWeight="700" fill="var(--green)">AUSTRALIA</text>
                  </g>
                </svg>

                <div className="dashboard-footer-info">
                  <div className="hub-tag">ORIGIN HUB: KASGANJ, UTTAR PRADESH (INDIA) 🌿</div>
                  <div className="hub-status-ticker">
                    <span className="live-pulse"></span> Multi-Port Transit Access Available via Mundra & Nhava Sheva
                  </div>
                </div>
              </div>

            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="cta-banner">
        <div className="container cta-inner">
          <div>
            <h2>Ready to Source Premium Indian Agro Products?</h2>
            <p>Join global buyers who trust Seth Mr. Sanwariya Agro Primes for quality, reliability, and timely delivery.</p>
          </div>
          <div className="cta-btns">
            <Link to="/contact" className="cta-btn-main">Send an Inquiry →</Link>
            <a href="https://wa.me/919528480615" target="_blank" rel="noreferrer" className="cta-btn-wa">
              💬 WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
